import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Lightbulb as LightbulbIcon,
  Science as ScienceIcon,
  Assessment as AssessmentIcon,
  RocketLaunch as RocketLaunchIcon,
  Public as PublicIcon,
  Psychology as PsychologyIcon,
  Biotech as BiotechIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const InnovationHubSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            CBP Innovation Hub
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Emerging Technology Testing and Scaling Platform
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The CBP Innovation Hub, managed by the Office of the Commissioner, serves as the agency's center for testing, 
            validating, and scaling emerging technologies with mission offices. The Hub provides a structured innovation 
            pipeline from concept to deployment, enabling rapid experimentation with cutting-edge technologies including AI, 
            robotics, biometrics, sensors, and data analytics while ensuring alignment with CBP's operational needs and 
            compliance requirements.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="Innovation Hub System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is Innovation Hub"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>technology innovation and pilot program</strong> that evaluates emerging technologies for CBP 
                    mission applications. Provides <strong>structured testing environments, proof-of-concept development, pilot 
                    programs</strong>, and scaling pathways for technologies including <strong>AI/ML, autonomous systems, advanced 
                    sensors, biometrics, blockchain</strong>, and data analytics solutions.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who manages Innovation Hub"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Managed by the <strong>Office of the Commissioner</strong> with collaboration from <strong>DHS Science and 
                    Technology Directorate (S&T), CBP mission offices</strong> (OFO, Border Patrol, Trade), <strong>OIT for 
                    technical integration</strong>, and partnerships with <strong>private sector vendors, startups, academic 
                    institutions</strong>, and other federal agencies.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was Innovation Hub established"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Formally established in <strong>2018</strong> to centralize innovation efforts. Expanded in <strong>2020-2021</strong> 
                    with increased funding and dedicated facilities. <strong>Active pilot programs</strong> run continuously with 
                    quarterly cohorts. Major technology demonstrations at <strong>annual Innovation Days</strong> and ongoing 
                    collaboration with DHS Silicon Valley Innovation Program (SVIP).
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is Innovation Hub located"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Primary hub at CBP headquarters</strong> in Washington, DC with <strong>testing facilities at select 
                    ports of entry</strong> and Border Patrol sectors. <strong>Virtual collaboration platform</strong> enables 
                    nationwide participation. Field testing occurs at <strong>operational environments</strong> including land 
                    borders, airports, seaports, and remote border areas.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why Innovation Hub exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To maintain <strong>CBP's technological edge</strong> in border security and trade facilitation. Provides 
                    <strong> structured pathway from innovation to operational deployment</strong>, reducing risk and accelerating 
                    adoption. Enables <strong>collaboration with private sector</strong> and access to cutting-edge technologies. 
                    Ensures <strong>mission-driven innovation</strong> aligned with operational needs and compliance requirements.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Innovation Focus Areas */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <LightbulbIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Innovation Focus Areas
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PsychologyIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Emerging Technology Pilots
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Testing and validation of cutting-edge technologies for CBP missions.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Artificial Intelligence:</strong> Computer vision, NLP, predictive analytics</Typography></li>
                <li><Typography variant="body2"><strong>Autonomous Systems:</strong> Drones, ground robots, surveillance platforms</Typography></li>
                <li><Typography variant="body2"><strong>Advanced Biometrics:</strong> Facial recognition, iris scanning, behavioral analytics</Typography></li>
                <li><Typography variant="body2"><strong>Sensor Technology:</strong> Chemical detection, radiation sensors, IoT devices</Typography></li>
                <li><Typography variant="body2"><strong>Blockchain:</strong> Supply chain tracking, credential verification</Typography></li>
                <li><Typography variant="body2"><strong>Quantum Computing:</strong> Cryptography and optimization applications</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScienceIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Innovation Testing & Validation
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Rigorous evaluation process ensuring operational readiness and compliance.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Proof of Concept:</strong> Initial feasibility and technical validation</Typography></li>
                <li><Typography variant="body2"><strong>Pilot Programs:</strong> Controlled operational testing at select sites</Typography></li>
                <li><Typography variant="body2"><strong>Performance Metrics:</strong> Quantitative evaluation against mission objectives</Typography></li>
                <li><Typography variant="body2"><strong>User Acceptance:</strong> Officer and agent feedback and training</Typography></li>
                <li><Typography variant="body2"><strong>Security Assessment:</strong> Cybersecurity and privacy compliance review</Typography></li>
                <li><Typography variant="body2"><strong>Cost-Benefit Analysis:</strong> ROI and total cost of ownership evaluation</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RocketLaunchIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Mission Office Collaboration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Partnership with operational components to ensure mission alignment.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Requirements Gathering:</strong> Operational needs assessment and prioritization</Typography></li>
                <li><Typography variant="body2"><strong>Co-Development:</strong> Joint design and testing with end users</Typography></li>
                <li><Typography variant="body2"><strong>Field Testing:</strong> Real-world operational environment validation</Typography></li>
                <li><Typography variant="body2"><strong>Training Development:</strong> User guides and training programs</Typography></li>
                <li><Typography variant="body2"><strong>Feedback Loops:</strong> Continuous improvement based on user input</Typography></li>
                <li><Typography variant="body2"><strong>Change Management:</strong> Organizational readiness and adoption support</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AutoAwesomeIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Technology Scaling Programs
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Pathways from successful pilots to enterprise-wide deployment.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Deployment Planning:</strong> Rollout strategy and timeline development</Typography></li>
                <li><Typography variant="body2"><strong>Integration Architecture:</strong> Enterprise system integration design</Typography></li>
                <li><Typography variant="body2"><strong>Procurement Support:</strong> Acquisition strategy and vendor management</Typography></li>
                <li><Typography variant="body2"><strong>Infrastructure Scaling:</strong> Network, compute, and storage expansion</Typography></li>
                <li><Typography variant="body2"><strong>Training at Scale:</strong> Enterprise-wide training program delivery</Typography></li>
                <li><Typography variant="body2"><strong>Sustainment Planning:</strong> Long-term operations and maintenance</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Innovation Pipeline */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <BiotechIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Innovation Pipeline Stages
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Stage</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Duration</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Activities</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Gate Criteria</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>1. Ideation</strong></TableCell>
              <TableCell>1-2 months</TableCell>
              <TableCell>Problem definition, market research, concept development</TableCell>
              <TableCell>Mission alignment, technical feasibility</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>2. Proof of Concept</strong></TableCell>
              <TableCell>3-6 months</TableCell>
              <TableCell>Technical validation, lab testing, initial prototyping</TableCell>
              <TableCell>Technical viability, security baseline</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>3. Pilot Program</strong></TableCell>
              <TableCell>6-12 months</TableCell>
              <TableCell>Field testing, user evaluation, performance measurement</TableCell>
              <TableCell>Operational effectiveness, user acceptance</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>4. Scaling Decision</strong></TableCell>
              <TableCell>2-3 months</TableCell>
              <TableCell>Cost-benefit analysis, deployment planning, funding approval</TableCell>
              <TableCell>Positive ROI, budget availability</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>5. Enterprise Deployment</strong></TableCell>
              <TableCell>12-24 months</TableCell>
              <TableCell>Procurement, integration, training, rollout</TableCell>
              <TableCell>Successful deployment, user adoption</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>6. Operations & Sustainment</strong></TableCell>
              <TableCell>Ongoing</TableCell>
              <TableCell>Maintenance, optimization, continuous improvement</TableCell>
              <TableCell>Performance targets, user satisfaction</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Active Innovation Programs */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Program Scale & Impact
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                50+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Pilots
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Technologies under evaluation
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                200+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vendor Partners
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Private sector collaborators
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                25+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scaled Technologies
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Deployed enterprise-wide
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                $50M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Investment
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Innovation funding
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Strategic Value
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Accelerated Innovation:</strong> Structured pathway from concept to deployment</Typography></li>
                <li><Typography variant="body2"><strong>Risk Mitigation:</strong> Pilot testing before major investment</Typography></li>
                <li><Typography variant="body2"><strong>Mission Alignment:</strong> Operational needs drive technology selection</Typography></li>
                <li><Typography variant="body2"><strong>Private Sector Access:</strong> Partnership with leading technology companies</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Competitive Advantage:</strong> Early access to emerging technologies</Typography></li>
                <li><Typography variant="body2"><strong>Cost Efficiency:</strong> Avoid costly failed deployments</Typography></li>
                <li><Typography variant="body2"><strong>Knowledge Transfer:</strong> Learning from pilots informs future programs</Typography></li>
                <li><Typography variant="body2"><strong>Cultural Change:</strong> Fosters innovation mindset across CBP</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Success Stories */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Recent Success Stories
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Chip label="Facial Recognition at Airports" color="success" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Deployed at 32 airports, 99% match rate</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Autonomous Surveillance Towers" color="success" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">200+ towers deployed on southwest border</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="AI-Powered Cargo Screening" color="success" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">40% reduction in inspection time</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Blockchain Trade Verification" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Pilot with 15 importers, 95% accuracy</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Drone Border Surveillance" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Testing in 5 Border Patrol sectors</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Chemical Detection Sensors" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Fentanyl detection at mail facilities</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default InnovationHubSystemDetail;
